import AccessTimeTwoToneIcon from '@mui/icons-material/AccessTimeTwoTone';
import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import ManageAccountsTwoToneIcon from '@mui/icons-material/ManageAccountsTwoTone';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsApplicationsTwoToneIcon from '@mui/icons-material/SettingsApplicationsTwoTone';
import { Box, Paper, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/permissionsContext';
import useIsMobile from '../../hooks/useIsMobile';
import { useMobileDrawerContext } from '../../contexts/mobile-drawer-context';

const TabItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const TabIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  marginBottom: theme.spacing(0.5),
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'color 0.2s ease-in-out',
  '& svg': {
    fontSize: theme.typography.pxToRem(20),
  },
}));

const TabLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'color 0.2s ease-in-out',
}));

export default function TabBarMobile({ variant = "floating" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { hasPermissionAction } = usePermissions();
  const isMobile = useIsMobile();
  const { isDrawerOpen } = useMobileDrawerContext() || {};
  const isSolid = variant === "solid";

  // Only show TabBar on mobile devices
  if (!isMobile) {
    return null;
  }

  // All possible tabs in order of priority
  // paths pode ser string ou array de strings para múltiplas rotas (novo/antigo layout)
  const allPossibleTabs = [
    {
      id: 'dashboard',
      text: 'Dashboard',
      icon: <DashboardTwoToneIcon />,
      paths: ['/'],
      permission: { resource: 'Dashboard', action: 'isinsert' },
      priority: 1
    },
    {
      id: 'vendas',
      text: 'Vendas',
      icon: <MonetizationOnTwoToneIcon />,
      paths: ['/vendas', '/listSale'],
      permission: { resource: 'Vendas', action: 'islist' },
      priority: 3
    },
    {
      id: 'orçamentos',
      text: 'Orçamento',
      icon: <AccessTimeTwoToneIcon />,
      paths: ['/orcamento', '/listBudget'],
      permission: null,
      priority: 8
    },
    {
      id: 'usuarios',
      text: 'Usuários',
      icon: <ManageAccountsTwoToneIcon />,
      paths: ['/user'],
      permission: { resource: 'Usuario', action: 'islist' },
      priority: 9
    },
    {
      id: 'configuracoes',
      text: 'Configurações',
      icon: <SettingsApplicationsTwoToneIcon />,
      paths: ['/listConfiguration'],
      permission: { resource: 'Configuração', action: 'islist' },
      priority: 10
    },
    {
      id: 'orcamento',
      text: 'Orçamento',
      icon: <ReceiptLongIcon />,
      paths: ['/availability'],
      permission: null,
      priority: 11
    }
  ];

  // Função para verificar se a rota atual corresponde aos paths do tab (case-sensitive)
  const isTabActive = (tab) => {
    const currentPath = location.pathname;
    const tabPaths = Array.isArray(tab.paths) ? tab.paths : [tab.paths];
    
    // Verificação case-sensitive - a rota deve corresponder exatamente ou começar com o path
    return tabPaths.some(path => {
      // Match exato
      if (currentPath === path) return true;
      // Match de sub-rota (ex: /vendas/123/editar corresponde a /vendas)
      if (currentPath.startsWith(path + '/')) return true;
      return false;
    });
  };

  // Filter tabs based on permissions and sort by priority
  const filteredTabs = allPossibleTabs.filter(tab => {
    if (!tab.permission) return true;
    return hasPermissionAction(tab.permission.resource, tab.permission.action);
  }).sort((a, b) => a.priority - b.priority);

  // Take only first 3 tabs - Dashboard, Vendas, Orçamentos
  const mainTabs = filteredTabs.slice(0, 3);

  // Determinar tab ativo baseado na rota atual (case-sensitive)
  const activeTab = mainTabs.find(tab => isTabActive(tab))?.id || mainTabs[0]?.id || 'dashboard';

  const handleTabClick = (tab) => {
    if (tab.paths) {
      // Navegar para o primeiro path disponível (geralmente o do novo layout)
      const targetPath = Array.isArray(tab.paths) ? tab.paths[0] : tab.paths;
      navigate(targetPath);
    }
  };

  return (
    <Paper
      sx={{
        position: isSolid ? 'relative' : 'fixed',
        bottom: isSolid ? 'auto' : (isDrawerOpen ? -100 : 0),
        left: isSolid ? 'auto' : 0,
        right: isSolid ? 'auto' : 0,
        width: '100%',
        zIndex: isSolid ? theme.zIndex.drawer : theme.zIndex.drawer + (isDrawerOpen ? -1 : 1),
        backgroundColor: theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRadius: isSolid ? 0 : '16px 16px 0 0',
        boxShadow: isSolid ? 'none' : '0 -2px 10px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', height: 70, py: 1, px:1, marginBottom: 1 }}>
        {mainTabs.map((tab) => (
          <TabItem
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => handleTabClick(tab)}
            style={{
              cursor: 'pointer'
            }}
          >
            <TabIcon active={activeTab === tab.id} style={{
              cursor: 'pointer'
            }}>
              {tab.icon}
            </TabIcon>
            <TabLabel active={activeTab === tab.id} style={{
              cursor: 'pointer'
            }}>
              {tab.text}
            </TabLabel>
          </TabItem>
        ))}
      </Box>
    </Paper>
  );
}
